import { Injectable } from '@angular/core';
import {
  FieldDefinition,
  getPrimitiveType,
  MATH_CONSTANT_SYMBOLS,
  MATH_CONSTANT_VALUES,
  MathConstant,
  PrimitiveTypeInfo,
  RepresentationCategory,
} from '@examdojo/core/ontology';

@Injectable({
  providedIn: 'root',
})
export class FieldTypeService {
  readonly katexOptions = {
    throwOnError: false,
    displayMode: false,
    strict: false,
  };

  readonly booleanOptions = [
    { label: 'True', value: true },
    { label: 'False', value: false },
  ];

  readonly categorySvgPaths: Record<RepresentationCategory, string> = {
    value: 'M 2 5 L 14 5 M 2 11 L 14 11 M 5.5 2 L 4.5 14 M 11.5 2 L 10.5 14',
    form: 'M 2 14 L 5 14 L 13 6 L 10 3 L 2 11 Z M 9 4 L 12 7',
    expression: 'M 4 13 L 8 3 L 12 13 M 5.5 10 L 10.5 10',
    object:
      'M 5 2 C 3 2 3 3 3 5 L 3 6 C 3 7 2 8 1 8 C 2 8 3 9 3 10 L 3 11 C 3 13 3 14 5 14 M 11 2 C 13 2 13 3 13 5 L 13 6 C 13 7 14 8 15 8 C 14 8 13 9 13 10 L 13 11 C 13 13 13 14 11 14',
    function: 'M 2 8 Q 5 2 8 8 Q 11 14 14 8',
    state: 'M 2 8 L 6 8 M 6 4 L 6 12 M 10 4 L 10 12 M 10 8 L 14 8',
  };

  getFieldTypeLabel(field: FieldDefinition): string {
    if (field.type === 'number') {
      return field.domain ?? 'number';
    }
    if (field.type === 'enum') {
      return 'enum';
    }
    if (field.type === 'constant') {
      return field.value;
    }
    if (field.type === 'string') {
      const format = 'format' in field ? field.format : undefined;
      if (format === 'latex') {
        return 'latex';
      }
    }
    if (field.type === 'array') {
      const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
      if (items?.['format'] === 'latex') {
        return 'Array<latex>';
      }
      if (items?.['type'] === 'number') {
        const domain = items['domain'] as string | undefined;
        return `Array<${domain ?? 'number'}>`;
      }
      if (items?.['type']) {
        return `Array<${String(items['type'])}>`;
      }
      return 'Array';
    }
    return field.type;
  }

  getPrimitiveTypeInfo(field: FieldDefinition): PrimitiveTypeInfo | undefined {
    let typeKey: string = field.type;

    if (field.type === 'number') {
      typeKey = field.domain ?? 'number';
    } else if (field.type === 'string' && 'format' in field && field.format === 'latex') {
      typeKey = 'latex';
    } else if (field.type === 'array') {
      return this.getArrayTypeInfo(field);
    }

    return getPrimitiveType(typeKey);
  }

  getTypeBadgeClasses(field: FieldDefinition): Record<string, boolean> {
    if (field.type === 'number') {
      const domain = 'domain' in field ? field.domain : undefined;
      if (domain === 'natural' || domain === 'integer') {
        return { 'bg-indigo-100': true, 'text-indigo-700': true };
      }
      return { 'bg-sky-100': true, 'text-sky-700': true };
    }
    if (field.type === 'string') {
      const format = 'format' in field ? field.format : undefined;
      if (format === 'latex') {
        return { 'bg-purple-100': true, 'text-purple-700': true };
      }
      return { 'bg-teal-100': true, 'text-teal-700': true };
    }
    if (field.type === 'array') {
      const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
      if (items?.['format'] === 'latex') {
        return { 'bg-purple-100': true, 'text-purple-700': true };
      }
      if (items?.['type'] === 'number') {
        return { 'bg-sky-100': true, 'text-sky-700': true };
      }
      if (items?.['type'] === 'string') {
        return { 'bg-teal-100': true, 'text-teal-700': true };
      }
      return { 'bg-emerald-100': true, 'text-emerald-700': true };
    }
    if (field.type === 'enum') {
      return { 'bg-amber-100': true, 'text-amber-700': true };
    }
    if (field.type === 'boolean') {
      return { 'bg-pink-100': true, 'text-pink-700': true };
    }
    if (field.type === 'constant') {
      return { 'bg-orange-100': true, 'text-orange-700': true };
    }
    return { 'bg-gray-100': true, 'text-gray-700': true };
  }

  getTypeStringBadgeClasses(type: string): Record<string, boolean> {
    switch (type) {
      case 'number':
      case 'real':
        return { 'bg-sky-100': true, 'text-sky-700': true };
      case 'integer':
      case 'natural':
        return { 'bg-indigo-100': true, 'text-indigo-700': true };
      case 'complex':
        return { 'bg-violet-100': true, 'text-violet-700': true };
      case 'string':
        return { 'bg-teal-100': true, 'text-teal-700': true };
      case 'latex':
      case 'symbolic':
        return { 'bg-purple-100': true, 'text-purple-700': true };
      case 'fraction':
      case 'percentage':
        return { 'bg-orange-100': true, 'text-orange-700': true };
      case 'enum':
        return { 'bg-amber-100': true, 'text-amber-700': true };
      case 'boolean':
        return { 'bg-pink-100': true, 'text-pink-700': true };
      case 'object':
      case 'array':
      case 'tuple':
        return { 'bg-emerald-100': true, 'text-emerald-700': true };
      case 'callable':
        return { 'bg-rose-100': true, 'text-rose-700': true };
      default:
        return { 'bg-gray-100': true, 'text-gray-700': true };
    }
  }

  getFieldTypeTooltip(field: FieldDefinition): string {
    if (field.type === 'number') {
      const domain = 'domain' in field ? field.domain : undefined;
      switch (domain) {
        case 'real':
          return 'ℝ (Real) - Any decimal number (e.g., -2.5, 0, π, 100.7)';
        case 'integer':
          return 'ℤ (Integer) - Whole numbers only (e.g., -3, -1, 0, 1, 5)';
        case 'natural':
          return 'ℕ (Natural) - Counting numbers starting from 1 (e.g., 1, 2, 3, 4, 5)';
        case 'positive':
          return 'ℝ⁺ (Positive) - Any number greater than 0 (e.g., 0.001, 1, π)';
        case 'negative':
          return 'ℝ⁻ (Negative) - Any number less than 0 (e.g., -0.001, -1, -π)';
        default:
          return 'A numeric value (real number by default)';
      }
    }
    if (field.type === 'enum') {
      return 'A finite set of valid string values';
    }
    if (field.type === 'boolean') {
      return 'A true/false value';
    }
    if (field.type === 'string') {
      const format = 'format' in field ? field.format : undefined;
      if (format === 'latex') {
        return 'Text formatted as LaTeX for mathematical notation';
      }
      if (format === 'markdown') {
        return 'Text formatted as Markdown';
      }
      return 'Plain text value';
    }
    if (field.type === 'constant') {
      return `Mathematical constant: ${this.getConstantSymbol(field.value)}`;
    }
    if (field.type === 'array') {
      const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
      if (items?.['format'] === 'latex') {
        return 'Array of LaTeX-formatted mathematical expressions';
      }
      if (items?.['type'] === 'number') {
        return 'Array of numbers';
      }
      if (items?.['type'] === 'string') {
        return 'Array of text strings';
      }
      return 'Array of values';
    }
    return 'Field type';
  }

  getConstantSymbol(value: string): string {
    const id = value as MathConstant;
    const symbol = MATH_CONSTANT_SYMBOLS[id];
    const numValue = MATH_CONSTANT_VALUES[id];
    if (symbol && numValue !== undefined) {
      if (numValue === Infinity) {
        return symbol;
      }
      return `${symbol} ≈ ${numValue.toFixed(5)}`;
    }
    return value;
  }

  getCategorySvgPath(category: RepresentationCategory | undefined): string {
    if (!category) {
      return 'M 8 2 A 6 6 0 1 0 8 14 A 6 6 0 1 0 8 2';
    }
    return this.categorySvgPaths[category] ?? 'M 8 2 A 6 6 0 1 0 8 14 A 6 6 0 1 0 8 2';
  }

  isLatexField(field: FieldDefinition): boolean {
    return field.type === 'string' && 'format' in field && field.format === 'latex';
  }

  isLatexArrayField(field: FieldDefinition): boolean {
    if (field.type !== 'array') {
      return false;
    }
    const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
    return items?.['format'] === 'latex';
  }

  getOptions(options: readonly string[]) {
    return options.map((opt) => ({ label: opt, value: opt }));
  }

  private getArrayTypeInfo(field: FieldDefinition): PrimitiveTypeInfo {
    const items = (field as unknown as Record<string, unknown>)['items'] as Record<string, unknown> | undefined;
    const isLatex = items?.['format'] === 'latex';
    const itemType = items?.['type'] as string | undefined;
    const domain = items?.['domain'] as string | undefined;

    if (isLatex) {
      return {
        type: 'Array<latex>',
        name: 'LaTeX Array',
        description: 'An array of mathematical expressions in LaTeX format.',
        example: '$[x^2, 2x + 1, \\frac{1}{x}]$',
      };
    }

    if (itemType === 'number') {
      const domainName = domain ?? 'number';
      return {
        type: `Array<${domainName}>`,
        name: `${this.capitalizeFirst(domainName)} Array`,
        description: `An array of ${domainName} values.`,
        example: '[1, 2, 3, 4, 5]',
      };
    }

    if (itemType === 'string') {
      return {
        type: 'Array<string>',
        name: 'String Array',
        description: 'An array of text values.',
        example: '["a", "b", "c"]',
      };
    }

    return {
      type: 'Array',
      name: 'Array',
      description: 'An array of values.',
      example: '[...]',
    };
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
